import { RangeSetBuilder } from "@codemirror/state";
import { Decoration, ViewPlugin, type DecorationSet, type EditorView, type ViewUpdate } from "@codemirror/view";

/**
 * Wrapped lines continue under the start of their text, as VS Code's
 * `editor.wrappingIndent: "same"` draws them: each indented visible line gets
 * its indent as padding and the same amount back as a negative text-indent,
 * so only the continuation rows move. The 1px is `.cm-line`'s own padding.
 */
export const wrapIndent = ViewPlugin.fromClass(
    class {
        decorations: DecorationSet;

        constructor(view: EditorView) {
            this.decorations = build(view);
        }

        update(update: ViewUpdate): void {
            if (update.docChanged || update.viewportChanged) this.decorations = build(update.view);
        }
    },
    { decorations: (plugin) => plugin.decorations },
);

const byColumns = new Map<number, Decoration>();

function build(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const tabSize = view.state.tabSize;
    for (const { from, to } of view.visibleRanges) {
        for (let pos = from; pos <= to;) {
            const line = view.state.doc.lineAt(pos);
            let columns = 0;
            for (const char of line.text) {
                if (char === " ") columns++;
                else if (char === "\t") columns += tabSize - (columns % tabSize);
                else break;
            }
            if (columns > 0) {
                let decoration = byColumns.get(columns);
                if (decoration === undefined) {
                    const style = `padding-left: calc(${columns}ch + 1px); text-indent: -${columns}ch`;
                    decoration = Decoration.line({ attributes: { style } });
                    byColumns.set(columns, decoration);
                }
                builder.add(line.from, line.from, decoration);
            }
            pos = line.to + 1;
        }
    }
    return builder.finish();
}
